import './style.scss';
import React from 'react';
import { Button } from 'react-bootstrap';
import { Text } from '../../atoms';

export type EvaluationKnowledgeCardInfoData = {
    isKnowledgeEvaluation: boolean;
    hasFinishedEvaluation: boolean;
    showEvaluationRetry: boolean;
    evaluationAvailableAttempts: number;
    hasEvaluationAvailableAttempts: boolean;
    oneAvaliableAttempts: boolean;
    evaluationGradeMessage: string;
};

export const getEvaluationKnowledgeCardInfo = (collection: any): EvaluationKnowledgeCardInfoData => {
    const evaluationAvailableAttempts = Number(collection?.data?.evaluationAvailableAttempts || 0);
    const evaluationMaxTries = Number(collection?.data?.evaluationMaxTries || 0);
    const evaluationMasteryScore = Number(collection?.data?.evaluationMasteryScore || 0);
    const evaluationGrade = Number(collection?.data?.questionnaire?.grade || 0);
    const lockAfterApproval = Number(collection?.data?.questionnaire?.lockAfterApproval || 0);
    const evaluationMasteryScoreReached = evaluationGrade >= evaluationMasteryScore;
    const isKnowledgeEvaluation = collection?.elementType === 'EVALUATION' && collection?.data?.evaluationType === 'KNOWLEDGE';
    const hasFinishedEvaluation = Boolean(collection?.data?.questionnaire?.finishDate);
    const lockAfterApprovalStatus = lockAfterApproval && evaluationMasteryScoreReached;

    return {
        isKnowledgeEvaluation,
        hasFinishedEvaluation,
        showEvaluationRetry:
            isKnowledgeEvaluation &&
            evaluationMaxTries !== 1 &&
            evaluationAvailableAttempts > 0 &&
            evaluationMaxTries !== evaluationAvailableAttempts &&
            !lockAfterApprovalStatus,
        evaluationAvailableAttempts,
        hasEvaluationAvailableAttempts: evaluationAvailableAttempts > 0,
        oneAvaliableAttempts: evaluationAvailableAttempts === 1,
        evaluationGradeMessage: evaluationMasteryScoreReached
            ? `Nota: ${evaluationGrade}. Nota necessária atingida`
            : `Nota: ${evaluationGrade}. Nota necessária <span class="evaluationKnowledgeGradeNotReached">não atingida</span>`,
    };
};

type EvaluationKnowledgeRetryProps = {
    info: EvaluationKnowledgeCardInfoData;
};

export const EvaluationKnowledgeRetry = ({ info }: EvaluationKnowledgeRetryProps) => {
    if (!info.showEvaluationRetry) {
        return null;
    }

    return (
        <div className="evaluationKnowledgeThumbContent">
            <div className="evaluationKnowledgeAttemptsText">
                Você tem{' '}
                <span>
                    {info.evaluationAvailableAttempts} {info.oneAvaliableAttempts ? 'tentativa' : 'tentativas'}
                </span>{' '}
                {info.oneAvaliableAttempts ? 'disponível' : 'disponíveis'}
            </div>
            <Button
                className="evaluationKnowledgeRetryButton"
                type="button"
                disabled={!info.hasEvaluationAvailableAttempts}
            >
                Tentar Novamente
            </Button>
        </div>
    );
};

type EvaluationKnowledgeGradeTextProps = {
    info: EvaluationKnowledgeCardInfoData;
    className?: string;
};

export const EvaluationKnowledgeGradeText = ({ info, className = '' }: EvaluationKnowledgeGradeTextProps) => {
    if (!info.isKnowledgeEvaluation) {
        return null;
    }

    return (
        <Text
            component="small"
            className={`evaluationKnowledgeGradeText ${className}`}
            text={info.evaluationGradeMessage}
        />
    );
};


export const getEvaluationProgressTexts = (collection) => {
    if (!collection || collection.elementType !== 'EVALUATION' || collection.data.evaluationType !== 'KNOWLEDGE') {
        return {
            concluidedText: '',
        };
    }

    const questionnaire = collection.data.questionnaire || null;
    const maxTries = Number(collection.data.evaluationMaxTries) || 1;

    if (!questionnaire) {
        return {
            concluidedText: `0 de ${maxTries} tentativa${maxTries > 1 ? 's' : ''}`,
        };
    }

    const grade = Number(questionnaire?.grade ?? 0);
    const masteryScore = Number(collection.data.evaluationMasteryScore);
    const attempts = Number(collection.data.evaluationAvailableAttempts);
    const lockAfterApproval = Number(questionnaire?.lockAfterApproval || 0);
    const attemptsRemaining = attempts;
    const hasAttemptsRemaining = maxTries !== 1 && attempts < maxTries;
    const hasReachedMasteryScore = grade >= masteryScore;
    const lockAfterApprovalStatus = lockAfterApproval && hasReachedMasteryScore;

    let concluidedText = '';

    if (
        !questionnaire?.finishDate ||
        grade === null ||
        grade === undefined ||
        (!questionnaire?.finishDate && attempts === 0)
    ) {
        concluidedText = `0 de ${maxTries} tentativa${maxTries > 1 ? 's' : ''}`;
    } else {
        const resultText = hasReachedMasteryScore ? 'Nota necessária atingida.' : 'Nota necessária não atingida.';
        const attemptsText = hasAttemptsRemaining
            ? `Você tem ${attemptsRemaining} tentativa${attemptsRemaining > 1 ? 's' : ''} para melhorar a sua nota.`
            : 'Todas as tentativas foram utilizadas.';
        const progressStatusClass = hasReachedMasteryScore ? 'progressStatusSuccess' : 'progressStatusFailed';

        const gradeText = grade ? `Nota: ${grade}. ` : '';
        concluidedText = `${gradeText}<span class='${progressStatusClass}'>${resultText}</span>`;
    }

    return {
        concluidedText,
    };
};